import { v5 as uuidV5 } from 'uuid';
import { DEFAULT_EVENTS_PAGE_LIMIT, EntryType, EventType, InvalidArgumentError, InvalidStateError, MAX_EVENTS_PAGE_LIMIT, MIN_EVENTS_PAGE_LIMIT, } from '#types/sdk';
export { DEFAULT_EVENTS_PAGE_LIMIT, MAX_EVENTS_PAGE_LIMIT, MIN_EVENTS_PAGE_LIMIT, } from '#types/sdk';
// UUIDv5 namespace for the deterministic event id - it must NEVER change, or every id shifts.
const EVENT_ID_NAMESPACE = '5e3f9b2a-1c84-4d6e-bf07-9a2c1e8d4f60';
// EventType (Core/Source/Metadata/KvHelpers/EventsKvHelper.h).
const WASM_EVENT_TYPE_TO_SDK = {
    1: EventType.FS_ENTRY_CREATED,
    2: EventType.FS_ENTRY_DELETED,
    3: EventType.FS_ENTRY_MODIFIED,
    4: EventType.FS_ENTRY_MOVED,
    5: EventType.FS_ENTRY_LINKED,
    6: EventType.FS_ENTRY_UNLINKED,
};
// FsEntry::Type (Core/Source/FileSystem/Common/FsEntry.h).
const WASM_ENTRY_TYPE_FILE = 1;
const WASM_ENTRY_TYPE_DIR = 2;
const WASM_ENTRY_TYPE_LINK = 3;
export function mapEventType(eventType) {
    const mapped = WASM_EVENT_TYPE_TO_SDK[eventType];
    if (mapped === undefined) {
        throw new InvalidStateError(`Unknown event type: ${eventType}`);
    }
    return mapped;
}
// FsEntry::Type also carries INVALID and DATASTREAM, and the API has no
// UNKNOWN entry type - anything unrecognized deliberately reports as FILE.
export function mapEntryType(entryType) {
    switch (entryType) {
        case WASM_ENTRY_TYPE_DIR:
            return EntryType.DIR;
        case WASM_ENTRY_TYPE_LINK:
            return EntryType.SYMLINK;
        case WASM_ENTRY_TYPE_FILE:
        default:
            return EntryType.FILE;
    }
}
function makeDeterministicEventId(filespaceId, eventCounter) {
    return uuidV5(`${filespaceId}:${eventCounter}`, EVENT_ID_NAMESPACE);
}
export const createEventsManager = (webFsEvents, workspaceId, filespaceId) => {
    return new EventsManagerImpl(webFsEvents, workspaceId, filespaceId);
};
class EventsManagerImpl {
    webFsEvents;
    workspaceId;
    filespaceId;
    constructor(webFsEvents, workspaceId, filespaceId) {
        this.webFsEvents = webFsEvents;
        this.workspaceId = workspaceId;
        this.filespaceId = filespaceId;
    }
    async listEventsPaginated(params = {}) {
        const limit = params.limit ?? DEFAULT_EVENTS_PAGE_LIMIT;
        const isValidLimit = Number.isInteger(limit) &&
            limit >= MIN_EVENTS_PAGE_LIMIT &&
            limit <= MAX_EVENTS_PAGE_LIMIT;
        if (!isValidLimit) {
            throw new InvalidArgumentError(`limit must be an integer in [${MIN_EVENTS_PAGE_LIMIT}, ${MAX_EVENTS_PAGE_LIMIT}]`);
        }
        const result = await this.webFsEvents.listEventsPaginated(limit, params.cursor);
        return {
            items: result.events.map((ev) => this.mapEvent(ev)),
            nextCursor: result.nextCursor,
        };
    }
    async tailCursor() {
        const result = await this.webFsEvents.tailCursor();
        return result.cursor;
    }
    mapEvent(ev) {
        const payload = {
            inodeId: ev.inodeId,
            entryType: mapEntryType(ev.entryType),
            entryId: ev.linkId,
            parentId: ev.parentId,
            ...(ev.previousParentId !== undefined && {
                previousParentId: ev.previousParentId,
            }),
            path: ev.path,
            ...(ev.previousPath !== undefined && {
                previousPath: ev.previousPath,
            }),
            size: Number(ev.size),
        };
        const actor = { actorId: ev.actorId };
        return {
            id: makeDeterministicEventId(this.filespaceId, ev.id),
            type: mapEventType(ev.eventType),
            timestamp: new Date(Number(ev.timestampMs)),
            filespaceId: this.filespaceId,
            workspaceId: this.workspaceId,
            actor,
            payload,
        };
    }
}
